/** @jsxImportSource @emotion/react */

import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import * as size from '@constant/size';
import * as color from '@constant/color';
import { ETheme } from '@datatype/type';
import {
  EApiCharacterType,
  EApiLanguageCode,
  EApiSubCharacterType,
  IClassroomCharacterData,
  IClassroomCharacterGroupData,
  IClassroomCharacterGroupWorkbookData,
} from '@datatype/iyagigo/api-type';
import { ICharacterGroup } from '@datatype/iyagigo/type';
import { useCharacterGroupWorkbooksQuery } from '@hook/iyagigo/useApi';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as classroomActions } from '@store/slice/classroom-slice';
import TextBlock from '@component/common/block/text-block';
import Line from '@component/common/line';
import IconBlock from '@component/common/block/icon-block';
import IconTextBlock from '@component/common/block/icon-text-block';
import ImageBlock from '@component/common/block/image-block';
import Canvas from '@design-system/canvas';
import Chip from '@design-system/chip';
import Spinner from '@design-system/spinner';
import ProgressBar from '@design-system/progress-bar';
import TextButton from '@design-system/text-button';
import Gap from '@component/common/gap';
import otterTestImage from '@asset/image/otter-test.webp';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { v4 as uuid4 } from 'uuid';

// ###################################
// CharacterHome — 글자 목록(행 선택) + 글자 종류(히라가나/가타카나) 변경 배너
// - classroom-home의 '글자' 탭 body (탭/템플릿은 classroom-home이 담당)
// - 행 클릭 시 학습 라우트(/ig/classroom/character)로 navigate (characterType/group 쿼리 전달)
//   → 단어집 home→study 패턴 미러
// ###################################

// 단어집 home의 "현재 단어집" 배너 패턴 (수달 + 라벨 + 변경하기)
const BannerContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: flex-start;
  gap: ${size.MARGIN_2};
  width: 100%;
`;

const BannerContent = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 8px;
`;

const TodayStudyContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  padding: ${size.MARGIN_1};
  border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.WHITE_1};
  box-shadow: ${color.SURFACE_SHADOW};
`;

const TodayLearningProgressLabel = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const GroupCardsContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 5px;
`;

const GroupCardContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${size.MARGIN_1};
  border-radius: ${size.BORDER_RADIUS_2};
  border: 1px solid ${color.GRAY_BOX_BORDER_COLOR};
  background-color: ${color.WHITE_1};
  cursor: pointer;
`;

const GroupCharacterTextContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
`;

const GroupLabelContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${size.MARGIN_1};
`;

// 청음/탁음 표 전환 세그먼트 탭
const SubCharacterTypeContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 5px;
  width: 100%;
`;

// 50음도 전체표 (보기 전용) — 행=세로, 단(あいうえお)=가로 5칸 그리드. 컴팩트하게.
const CharacterTableContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  border-radius: ${size.BORDER_RADIUS_2};
  border: ${size.BORDER_WIDTH_1} solid ${color.GRAY_BOX_BORDER_COLOR};
`;

// 행(行) 묶음 — 글자 grid + 아래 구분선(Line)을 한 단위로
const CharacterRowContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;

// 글자 5칸 grid (표 폭 꽉 채움)
const CharacterRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  width: 100%;
  padding: 2px 0 2px 0;
`;

// 글자 + 한글 발음(작게) 세로 배치 (글자 아래 발음)
const CharacterCellContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
`;

// 접힘 상태에서 노출할 행 수 (그 이상은 '펼쳐보기'로 확장)
const COLLAPSED_ROW_COUNT = 3;

// 펼치기/접기 토글 — 표 하단 가운데, 옅은 그레이 배경 띠
const ExpandToggleContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 5px 0;
  border-top: ${size.BORDER_WIDTH_1} solid ${color.GRAY_4};
  background-color: ${color.GRAY_5};
  cursor: pointer;
`;

// 가나 전체표 (보기 전용) — 행별로 5칸 그리드, 빈 자리는 빈 셀로 정렬 유지
// groups를 받아 청음/탁음 표를 같은 컴포넌트로 렌더 (symbol이 이미 해당 글자종류 글자)
// 기본은 COLLAPSED_ROW_COUNT행까지만, 하단 '펼쳐보기'로 전체 확장
const CharacterTable: React.FC<{ groups: ICharacterGroup[] }> = ({ groups }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const isCollapsible = groups.length > COLLAPSED_ROW_COUNT;
  const visibleGroups = isExpanded ? groups : groups.slice(0, COLLAPSED_ROW_COUNT);

  return (
    <CharacterTableContainer>
      {visibleGroups.map((group, groupIdx) => (
        <CharacterRowContainer key={uuid4()}>
          <CharacterRow>
            {Array.from({ length: 5 }).map((_, idx) => {
              const character = group.characters[idx];
              return (
                <CharacterCellContainer key={uuid4()}>
                  {character && (
                    <>
                      <TextBlock
                        text={character.symbol}
                        design={{ text: { size: 'large', color: color.BLACK_2 }, box: {} }}
                      />
                      <TextBlock
                        text={character.pronunciation.ko ?? ''}
                        design={{ text: { size: 'tiny', color: color.GRAY_1 }, box: {} }}
                      />
                    </>
                  )}
                </CharacterCellContainer>
              );
            })}
          </CharacterRow>
          {/* 행 사이 구분선 — width를 줄여 좌우 여백(글자는 꽉, 선만 안쪽). 마지막 행 제외 */}
          {groupIdx < visibleGroups.length - 1 && (
            <Line color={color.GRAY_4} width={`calc(100% - ${size.MARGIN_1} * 2)`} margin='0 auto' />
          )}
        </CharacterRowContainer>
      ))}
      {isCollapsible && (
        <ExpandToggleContainer onClick={() => setIsExpanded((prev) => !prev)}>
          <IconTextBlock
            icon={isExpanded ? 'up' : 'down'}
            text={isExpanded ? '접기' : '더보기'}
            arrangement='text-icon'
            design={{
              text: { size: 'small', color: color.GRAY_1, bold: true, underline: false },
              icon: { size: 'small', color: color.GRAY_1, kind: 'outlined' },
              box: { event: { cursor: 'pointer' }, gap: '2px' },
            }}
          />
        </ExpandToggleContainer>
      )}
    </CharacterTableContainer>
  );
};

// 행 라벨 — 일반 행은 'あ행', 작게 쓰는 글자 행은 촉음/요음 (subCharacterType로 판별)
const getGroupLabel = (characters: IClassroomCharacterData[], subCharacterType: EApiSubCharacterType | null): string => {
  const first = characters[0];
  if (!first) return '';
  if (subCharacterType === EApiSubCharacterType.SOKUON) return '촉음';
  if (subCharacterType === EApiSubCharacterType.YOON) return '요음';
  return `${first.symbol}행`;
};

// CharacterGroup 응답을 화면용 그룹으로 변환
const groupCharacters = (characterGroups: IClassroomCharacterGroupData[]): ICharacterGroup[] => {
  return characterGroups.map((group) => {
    const sorted = [...group.characters].sort((a, b) => a.characterOrder - b.characterOrder);
    return {
      key: group.groupKey,
      label: getGroupLabel(sorted, group.subCharacterType),
      characters: sorted,
    };
  });
};

const CHARACTER_STUDY_SECTIONS = [
  { subCharacterType: EApiSubCharacterType.SEION, label: '청음' },
  { subCharacterType: EApiSubCharacterType.DAKUON, label: '탁음·반탁음' },
  { subCharacterType: EApiSubCharacterType.SOKUON, label: '촉음·요음' },
] as const;

const getIsSubCharacterTypeWorkbook = (
  workbook: IClassroomCharacterGroupWorkbookData,
  subCharacterType: EApiSubCharacterType,
) => {
  const workbookSubCharacterType = workbook.characterGroup.subCharacterType;
  if (subCharacterType === EApiSubCharacterType.SEION) {
    return workbookSubCharacterType === EApiSubCharacterType.SEION;
  }
  if (subCharacterType === EApiSubCharacterType.DAKUON) {
    return (
      workbookSubCharacterType === EApiSubCharacterType.DAKUON ||
      workbookSubCharacterType === EApiSubCharacterType.HANDAKUON
    );
  }
  return (
    workbookSubCharacterType === EApiSubCharacterType.SOKUON || workbookSubCharacterType === EApiSubCharacterType.YOON
  );
};

const findRecommendedWorkbook = (workbooks: IClassroomCharacterGroupWorkbookData[]) => {
  for (const section of CHARACTER_STUDY_SECTIONS) {
    const workbook = workbooks.find(
      (item) => !item.studiedAt && getIsSubCharacterTypeWorkbook(item, section.subCharacterType),
    );
    if (workbook) return { workbook, ...section };
  }
  return null;
};

const CharacterHome: React.FC<any> = () => {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();

  // 글자종류(히라가나/가타카나) / 표 종류(청음·탁음) — redux(classroom-slice) 구독. 탭 이탈 후 유지.
  const characterType =
    useTypedSelector((state) => state.rootReducer?.classroomReducer?.characterType) ?? EApiCharacterType.HIRAGANA;
  const subCharacterType =
    useTypedSelector((state) => state.rootReducer?.classroomReducer?.subCharacterType) ?? EApiSubCharacterType.SEION;
  const isHiragana = characterType === EApiCharacterType.HIRAGANA;
  const isSeion = subCharacterType === EApiSubCharacterType.SEION;
  // 촉음·요음 탭은 SOKUON을 탭 마커로 저장 (표에는 SOKUON+YOON 함께 노출)
  const isSmall = subCharacterType === EApiSubCharacterType.SOKUON || subCharacterType === EApiSubCharacterType.YOON;
  const currentSectionType = isSeion
    ? EApiSubCharacterType.SEION
    : isSmall
      ? EApiSubCharacterType.SOKUON
      : EApiSubCharacterType.DAKUON;
  const oppositeCharacterType = isHiragana ? EApiCharacterType.KATAKANA : EApiCharacterType.HIRAGANA;

  const { data: characterGroupWorkbooksResponse, isLoading } = useCharacterGroupWorkbooksQuery(
    EApiLanguageCode.JAPANESE,
    characterType,
  );
  const { data: oppositeCharacterGroupWorkbooksResponse, isLoading: isOppositeCharacterTypeLoading } =
    useCharacterGroupWorkbooksQuery(EApiLanguageCode.JAPANESE, oppositeCharacterType);
  const characterGroupWorkbooks: IClassroomCharacterGroupWorkbookData[] = characterGroupWorkbooksResponse?.data ?? [];
  const oppositeCharacterGroupWorkbooks: IClassroomCharacterGroupWorkbookData[] =
    oppositeCharacterGroupWorkbooksResponse?.data ?? [];
  const visibleWorkbooks = characterGroupWorkbooks.filter((workbook) =>
    getIsSubCharacterTypeWorkbook(workbook, currentSectionType),
  );
  const visibleCharacterGroups = visibleWorkbooks.map((workbook) => workbook.characterGroup);
  const tableGroups = groupCharacters(visibleCharacterGroups);
  const currentWorkbook = visibleWorkbooks.find((workbook) => !workbook.studiedAt) ?? null;
  const studiedCount = visibleWorkbooks.filter((workbook) => !!workbook.studiedAt).length;
  const currentSection = CHARACTER_STUDY_SECTIONS.find((section) => section.subCharacterType === currentSectionType);
  const currentTypeRecommendation = findRecommendedWorkbook(characterGroupWorkbooks);
  const oppositeTypeRecommendation = findRecommendedWorkbook(oppositeCharacterGroupWorkbooks);
  const recommendation =
    currentWorkbook && currentSection
      ? { workbook: currentWorkbook, ...currentSection, characterType }
      : currentTypeRecommendation
        ? { ...currentTypeRecommendation, characterType }
        : oppositeTypeRecommendation
          ? { ...oppositeTypeRecommendation, characterType: oppositeCharacterType }
          : null;
  const recommendationGroup = recommendation
    ? (groupCharacters([recommendation.workbook.characterGroup])[0] ?? null)
    : null;
  const isCurrentSectionRecommendation =
    recommendation?.characterType === characterType && recommendation.subCharacterType === currentSectionType;
  const isRecommendationLoading = isLoading || (!currentTypeRecommendation && isOppositeCharacterTypeLoading);
  const recommendationCharacterTypeLabel =
    recommendation?.characterType === EApiCharacterType.HIRAGANA ? '히라가나' : '가타카나';
  const recommendationTitle = isRecommendationLoading
    ? '학습 정보를 불러오고 있어요'
    : recommendation
      ? isCurrentSectionRecommendation
        ? `<strong>${recommendationGroup?.label ?? ''}</strong>부터 시작해요`
        : recommendation.characterType === characterType
          ? `다음은 <strong>${recommendation.label}</strong>을 학습해 보세요`
          : `다음은 <strong>${recommendationCharacterTypeLabel} ${recommendation.label}</strong>을 학습해 보세요`
      : '히라가나와 가타카나를 모두 학습했어요';

  // 행 클릭 → 학습 라우트로 (단어집의 vocabularyBookKey 패턴 미러: characterType/group 쿼리 전달)
  const handleGroupClick = (groupKey: string, targetCharacterType = characterType) => {
    navigate(
      `${IYAGIGO_PATH.CLASSROOM_CHARACTER_PATH}?language=${EApiLanguageCode.JAPANESE.toLowerCase()}&characterType=${targetCharacterType}&group=${groupKey}`,
    );
  };

  const handleRecommendedStudy = () => {
    if (recommendation && recommendationGroup) {
      dispatch(classroomActions.storeCharacterType(recommendation.characterType));
      dispatch(classroomActions.storeSubCharacterType(recommendation.subCharacterType));
      handleGroupClick(recommendationGroup.key, recommendation.characterType);
      return;
    }
    const firstGroup = tableGroups[0];
    if (firstGroup) handleGroupClick(firstGroup.key);
  };

  const Groups = tableGroups.map((group) => {
    const workbook = visibleWorkbooks.find((item) => item.characterGroup.groupKey === group.key);
    const isStudied = !!workbook?.studiedAt;
    const groupLabel = group.label;
    const charPreview = group.characters.map((c) => c.symbol).join(' ');
    return (
      <GroupCardContainer key={uuid4()} onClick={() => handleGroupClick(group.key)}>
        <GroupCharacterTextContainer>
          <GroupLabelContainer>
            <TextBlock
              text={groupLabel}
              design={{ text: { size: 'medium', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
            />
            {isStudied && <Chip text='학습 완료' size='tiny' usage='static' kind='outlined' theme={ETheme.GREEN} />}
          </GroupLabelContainer>
          <TextBlock
            text={charPreview}
            design={{ text: { size: 'small', color: color.GRAY_1 }, box: { alignX: 'left' } }}
          />
        </GroupCharacterTextContainer>
        <IconBlock icon='right' design={{ icon: { kind: 'outlined', size: 'medium', color: color.GRAY_1 }, box: {} }} />
      </GroupCardContainer>
    );
  });

  return (
    <>
      <BannerContainer>
        <ImageBlock src={otterTestImage} alt='otter' design={{ image: { height: '80px' } }} />
        <BannerContent>
          <TextBlock text='현재 글자' design={{ text: { size: 'medium', color: color.BLACK_2 } }} />
          <TextBlock
            text={isHiragana ? '히라가나' : '가타카나'}
            design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }}
          />
          <IconTextBlock
            icon={'swap'}
            text={isHiragana ? '가타카나 보기' : '히라가나 보기'}
            arrangement='text-icon'
            design={{
              text: { size: 'tiny', color: color.GRAY_1, underline: false },
              icon: { size: 'tiny', color: color.GRAY_1, kind: 'outlined' },
              box: { event: { cursor: 'pointer' }, gap: '2px' },
            }}
            click={{
              handler: () =>
                dispatch(
                  classroomActions.storeCharacterType(
                    isHiragana ? EApiCharacterType.KATAKANA : EApiCharacterType.HIRAGANA,
                  ),
                ),
            }}
          />
        </BannerContent>
      </BannerContainer>
      <Gap height={size.MARGIN_1} />
      <Canvas theme={ETheme.BROWN} shape={'square'} width='100%'>
        <TextBlock
          text={`<strong style="color: #A26939;">${isHiragana ? '히라가나' : '가타카나'}</strong>를 한 행씩 익혀보세요`}
          design={{ text: { size: 'medium', color: color.BLACK_2, bold: false }, box: { alignX: 'left' } }}
        />
      </Canvas>
      <Gap height={size.MARGIN_2} />
      <TextBlock
        text='오늘의 학습'
        design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
      />
      <Gap height={size.MARGIN_1} />
      <TodayStudyContainer>
        <TextBlock
          text={recommendationTitle}
          design={{ text: { size: 'large', color: color.BLACK_2, bold: false }, box: { alignX: 'left' } }}
        />
        <Gap height='5px' />
        <TextBlock
          text={
            recommendationGroup
              ? recommendationGroup.characters.map((character) => character.symbol).join(' ')
              : isRecommendationLoading
                ? ''
                : '글자표에서 원하는 행을 다시 학습할 수 있어요'
          }
          design={{ text: { size: 'medium', color: color.GRAY_1 }, box: { alignX: 'left' } }}
        />
        <Gap height={size.MARGIN_1} />
        <TodayLearningProgressLabel>
          <TextBlock text='전체 진도' design={{ text: { size: 'small', color: color.BLACK_4, bold: true }, box: {} }} />
          <TextBlock
            text={`${studiedCount} / ${visibleWorkbooks.length}행`}
            design={{ text: { size: 'tiny', color: color.GRAY_1 }, box: {} }}
          />
        </TodayLearningProgressLabel>
        <Gap height='5px' />
        <ProgressBar
          ratio={visibleWorkbooks.length > 0 ? studiedCount / visibleWorkbooks.length : 0}
          theme={ETheme.BROWN}
          width='100%'
          height='6px'
        />
        <Gap height={size.MARGIN_1} />
        <TextButton
          text={recommendation ? '시작하기' : '다시 학습하기'}
          theme={ETheme.BROWN}
          size='medium'
          shape='pill'
          width='100%'
          disabled={isRecommendationLoading || (!recommendation && tableGroups.length === 0)}
          handleClick={handleRecommendedStudy}
        />
      </TodayStudyContainer>
      <Gap height={size.MARGIN_2} />
      <TextBlock
        text='글자표'
        design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
      />
      <Gap height={size.MARGIN_1} />
      <SubCharacterTypeContainer>
        <Chip
          text='청음'
          size='small'
          theme={ETheme.BROWN}
          isSelected={isSeion}
          handleClick={() => dispatch(classroomActions.storeSubCharacterType(EApiSubCharacterType.SEION))}
        />
        <Chip
          text='탁음·반탁음'
          size='small'
          theme={ETheme.BROWN}
          isSelected={!isSeion && !isSmall}
          handleClick={() => dispatch(classroomActions.storeSubCharacterType(EApiSubCharacterType.DAKUON))}
        />
        <Chip
          text='촉음·요음'
          size='small'
          theme={ETheme.BROWN}
          isSelected={isSmall}
          handleClick={() => dispatch(classroomActions.storeSubCharacterType(EApiSubCharacterType.SOKUON))}
        />
      </SubCharacterTypeContainer>
      <Gap height={size.MARGIN_1} />
      {isLoading ? (
        <Spinner size='28px' color={color.BROWN_1} />
      ) : (
        <>
          <CharacterTable groups={tableGroups} />
          <Gap height={size.MARGIN_2} />
          <TextBlock
            text='글자 학습'
            design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
          />
          <Gap height={size.MARGIN_1} />
          <GroupCardsContainer>{Groups}</GroupCardsContainer>
        </>
      )}
    </>
  );
};

export default CharacterHome;
